import * as React from 'react';
import styled from 'styled-components';
import { format } from 'date-fns';

import { DatePicker, Loader } from '../../atoms';
import { DEFAULT_DATE_FORMAT } from '../../constants';
import {
  useGetAllInspectorsQuery,
  useSetBulkInspectionsMutation,
} from '../../redux/rtkQuery/apiSlice';
import { getPermitUsers } from '../../api/users';
import { getInspectionTypeByID } from '../../api/inspectionTypes';
import { getPermitInspectionById, getTradesByProjectType } from '../../api/inspections';
import { GET_ALL_QUERY_PARAMS } from '../../constants';
import { getEffectiveTradeIds, TradeRef } from '../../utils/inspectorTradeQualification';
import { USER_TYPES_VALUES, CLIENT_TYPES_VALUES } from '../../constants/forms';

export interface BulkScheduleRow {
  id: number;
  titleId: number | string | null | undefined;
  // EA-1123 parity: trades of this inspection so the Inspector dropdown can be
  // filtered to qualified inspectors. `trades` are trade IDs; `inspectionTypeId`
  // is the numeric type id used as a fallback to resolve trades when the row
  // doesn't carry them directly.
  trades?: number[];
  inspectionTypeId?: number | null;
}

interface ContactOption {
  id: string;
  label: string;
  cellPhone: string;
  phoneNumber: string;
}

interface Props {
  selectedInspections: BulkScheduleRow[];
  permitId?: number | string;
  onClose: () => void;
  onSuccess: (updatedCount: number, failureCount: number) => void;
}

// AM / PM ids match the backend PERIOD_CHOICES (AM = 0, PM = 1).
const PERIOD_OPTIONS = [
  { value: 0, label: 'AM' },
  { value: 1, label: 'PM' },
];

const BulkScheduleInspectionsModal = ({
  selectedInspections,
  permitId,
  onClose,
  onSuccess,
}: Props) => {
  const { data: inspectors = [], isFetching } = useGetAllInspectorsQuery(null);
  const [setBulkInspections, { isLoading }] = useSetBulkInspectionsMutation();

  const [date, setDate] = React.useState<Date | null>(null);
  const [period, setPeriod] = React.useState<number | ''>('');
  const [inspectorId, setInspectorId] = React.useState<number | ''>('');
  const [error, setError] = React.useState<string>('');

  // Optional contact section. Picking a contact pre-fills its phones (editable);
  // the phones write back to the contact, mirroring the single-inspection form.
  const [contacts, setContacts] = React.useState<ContactOption[]>([]);
  const [contactsLoading, setContactsLoading] = React.useState<boolean>(false);
  const [contactId, setContactId] = React.useState<string>('');
  const [cellPhone, setCellPhone] = React.useState<string>('');
  const [phoneNumber, setPhoneNumber] = React.useState<string>('');
  // Per-field required errors — only enforced once a contact is chosen.
  const [cellPhoneError, setCellPhoneError] = React.useState<string>('');
  const [phoneNumberError, setPhoneNumberError] = React.useState<string>('');

  // Activity Contact options come from the PERMIT's contacts, mirroring the
  // single-inspection edit form (getPermitUsers + INDIVIDUAL filter) — not the
  // project's contacts.
  React.useEffect(() => {
    if (!permitId) return;
    setContactsLoading(true);
    getPermitUsers({ permit_id: permitId, userRole: USER_TYPES_VALUES.CLIENT })
      .then(res => {
        const opts: ContactOption[] = ((res?.data as any[]) || [])
          .filter(user => user?.isActive)
          .filter(
            user =>
              Number(user?.clientType) === CLIENT_TYPES_VALUES.INDIVIDUAL,
          )
          .map(user => {
            const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
            const label =
              [name, user.email, user.companyName || user.parentCompany]
                .filter(Boolean)
                .join(' - ') || `Contact ${user.id}`;
            return {
              id: String(user.id),
              label,
              cellPhone: user.mobilePhoneNumber || '',
              phoneNumber: user.phoneNumber || '',
            };
          });
        setContacts(opts);
      })
      .catch(() => {})
      .finally(() => setContactsLoading(false));
  }, [permitId]);

  const handleContactChange = (id: string) => {
    setContactId(id);
    const selected = contacts.find(contact => contact.id === id);
    setCellPhone(selected?.cellPhone || '');
    setPhoneNumber(selected?.phoneNumber || '');
    // Surface the required messages right away when the picked contact has no
    // phone(s) on file; clear them when the contact is cleared.
    if (id !== '') {
      setCellPhoneError(
        selected?.cellPhone ? '' : 'Cell phone is a required field',
      );
      setPhoneNumberError(
        selected?.phoneNumber ? '' : 'Phone number is a required field',
      );
    } else {
      setCellPhoneError('');
      setPhoneNumberError('');
    }
  };

  // Only rows that carry a title id can be scheduled — the backend needs it to
  // resolve the per-title "Scheduled" status.
  const schedulableRows = React.useMemo(
    () => selectedInspections.filter(row => row.titleId != null && row.titleId !== ''),
    [selectedInspections],
  );
  const skippedCount = selectedInspections.length - schedulableRows.length;

  const count = schedulableRows.length;
  const inspectionLabel = count === 1 ? 'inspection' : 'inspections';

  // EA-1123 parity with EA-1195: qualifying an inspector must account for the
  // Residential-license expansion AND the project-type trade set (1&2 dwelling
  // vs multifamily/commercial), not just a raw inspector-trade ∩ inspection-
  // trade intersection.
  //
  // All selected rows belong to the SAME permit/project, so there is a single
  // project type. We resolve it from one inspection, then load the trades valid
  // for that project type.
  const [requiredTrades, setRequiredTrades] = React.useState<number[]>([]);
  const [projectTypeTrades, setProjectTypeTrades] = React.useState<TradeRef[]>([]);
  const typeTradeCache = React.useRef<Record<number, number[]>>({});

  // Load the project-type trade set once (residential/commercial gate).
  React.useEffect(() => {
    let cancelled = false;
    const first = selectedInspections[0];
    if (!first?.id) {
      setProjectTypeTrades([]);
      return undefined;
    }
    getPermitInspectionById({ ...GET_ALL_QUERY_PARAMS, id: first.id })
      .then((res: any) => {
        const projectType = res?.data?.projectType || null;
        return getTradesByProjectType(projectType);
      })
      .then((tradesResponse: any) => {
        if (cancelled) return;
        setProjectTypeTrades((tradesResponse?.data?.trades as TradeRef[]) || []);
      })
      .catch(() => {
        if (!cancelled) setProjectTypeTrades([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedInspections]);

  // Union of the trades required across all selected inspections.
  React.useEffect(() => {
    let cancelled = false;
    if (!selectedInspections.length) {
      setRequiredTrades([]);
      return undefined;
    }

    const normalize = (raw: any): number[] =>
      (Array.isArray(raw) ? raw : []).map((t: any) =>
        Number(typeof t === 'object' && t !== null ? t.id : t),
      );

    const resolveRowTrades = async (row: BulkScheduleRow): Promise<number[]> => {
      const inline = normalize(row.trades);
      if (inline.length) return inline;
      const typeId = row.inspectionTypeId ? Number(row.inspectionTypeId) : null;
      if (!typeId || Number.isNaN(typeId)) return [];
      if (typeTradeCache.current[typeId]) return typeTradeCache.current[typeId];
      try {
        const res: any = await getInspectionTypeByID(typeId);
        const trades = normalize(res?.data?.trades || res?.data?.tradeIds);
        typeTradeCache.current[typeId] = trades;
        return trades;
      } catch (e) {
        return [];
      }
    };

    Promise.all(selectedInspections.map(resolveRowTrades)).then(results => {
      if (cancelled) return;
      const union = new Set<number>();
      results.forEach(trades => trades.forEach(t => {
        if (!Number.isNaN(t)) union.add(t);
      }));
      setRequiredTrades(Array.from(union));
    });

    return () => {
      cancelled = true;
    };
  }, [selectedInspections]);

  // Intersection across the batch, using each inspector's EFFECTIVE trades
  // (Residential expanded) and gated by the project-type trade set. An inspector
  // must cover every required trade. If trades can't be resolved, show all.
  const qualifiedInspectors = React.useMemo(() => {
    if (!requiredTrades.length) return inspectors;
    const projectSet = new Set<number>((projectTypeTrades || []).map(t => Number(t.id)));
    return (inspectors as any[]).filter((insp: any) => {
      const effIds = getEffectiveTradeIds(insp.trade, projectTypeTrades);
      return requiredTrades.every((tradeId: number) => {
        // Gate on the project-type trade set when we have one: a required trade
        // outside the project's valid set can never qualify (e.g. a bare
        // Residential license on a commercial project).
        if (projectSet.size && !projectSet.has(tradeId)) return false;
        return effIds.includes(tradeId);
      });
    });
  }, [inspectors, requiredTrades, projectTypeTrades]);

  // If the currently selected inspector is filtered out (no longer qualified),
  // clear the selection so an unqualified inspector can't be submitted.
  React.useEffect(() => {
    if (
      inspectorId !== '' &&
      !(qualifiedInspectors as any[]).some((i: any) => Number(i.value) === Number(inspectorId))
    ) {
      setInspectorId('');
    }
  }, [qualifiedInspectors, inspectorId]);

  // Every field is optional — an inspection is only Scheduled once it has both a date
  // and an inspector, so either one may be set on its own. We just need at least one
  // value to apply, otherwise there is nothing to submit.
  const hasSomethingToApply =
    Boolean(date) || period !== '' || inspectorId !== '' || contactId !== '';
  const isValid = hasSomethingToApply && count > 0;

  const handleSubmit = async () => {
    if (!isValid) {
      setError('Choose a date, AM/PM, inspector, or contact to continue.');
      return;
    }
    setError('');

    // Once a contact is chosen, both phones are required (they write back to
    // the contact) — block submit with per-field messages if either is empty.
    if (contactId !== '') {
      const missingPhone = phoneNumber.trim() === '';
      const missingCell = cellPhone.trim() === '';
      setPhoneNumberError(
        missingPhone ? 'Phone number is a required field' : '',
      );
      setCellPhoneError(missingCell ? 'Cell phone is a required field' : '');
      if (missingPhone || missingCell) {
        return;
      }
    }

    const payload: any = {
      inspections: schedulableRows.map(row => ({
        inspectionId: row.id,
        title_id: row.titleId,
      })),
    };
    // Every field is optional — only send the ones that were chosen.
    if (date) {
      payload.date = format(date as Date, 'yyyy-MM-dd');
    }
    if (period !== '') {
      payload.period = period;
    }
    if (inspectorId !== '') {
      payload.inspectorId = inspectorId;
    }
    // Contact + phones are optional. Phones live on the contact, so they only
    // apply when a contact is chosen. Keys match what the backend reads.
    if (contactId !== '') {
      payload.contact = Number(contactId);
      if (phoneNumber !== '') {
        payload.phone_number = phoneNumber;
      }
      if (cellPhone !== '') {
        payload.mobile_phone_number = cellPhone;
      }
    }

    try {
      const result: any = await setBulkInspections(payload);
      if (result?.error) {
        setError('Something went wrong while scheduling. Please try again.');
        return;
      }
      const failures = result?.data?.failures?.length || 0;
      onSuccess(count - failures, failures);
    } catch (e) {
      setError('Something went wrong while scheduling. Please try again.');
    }
  };

  return (
    <ModalBody>
      <Header>
        <Eyebrow>Bulk action</Eyebrow>
        <Title>Bulk Schedule Inspections</Title>
        <Subtitle>
          Apply the selected values to <Strong>{count}</Strong>&nbsp;
          {inspectionLabel}. All fields are optional. Complete only the fields
          you want to update. The status changes to{' '}
          <StatusChip>Scheduled</StatusChip> only when both an Inspection Date
          and Inspector are assigned; otherwise, it remains{' '}
          <StatusChip>Not Scheduled</StatusChip>.
        </Subtitle>
      </Header>

      <Field>
        <FieldLabel>Inspection Date</FieldLabel>
        <DatePickerShell>
          <DatePicker
            portalPopper
            selected={date as Date}
            onChange={(value: Date) => setDate(value)}
            dateFormat={DEFAULT_DATE_FORMAT}
            placeholderText="Select a date"
          />
        </DatePickerShell>
      </Field>

      <Field>
        <FieldLabel>AM / PM</FieldLabel>
        <SelectShell>
          <StyledSelect
            value={period}
            onChange={e =>
              setPeriod(e.target.value === '' ? '' : Number(e.target.value))
            }
          >
            <option value="">Select AM/PM…</option>
            {PERIOD_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </StyledSelect>
          <Caret aria-hidden>▾</Caret>
        </SelectShell>
      </Field>

      <Field>
        <FieldLabel>Inspector</FieldLabel>
        <SelectShell>
          <StyledSelect
            $clearable={inspectorId !== ''}
            value={inspectorId}
            onChange={e =>
              setInspectorId(
                e.target.value === '' ? '' : Number(e.target.value),
              )
            }
          >
            <option value="">Select inspector…</option>
            {qualifiedInspectors.map((item: any) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </StyledSelect>
          {inspectorId !== '' && (
            <ClearButton
              type="button"
              aria-label="Clear inspector"
              onClick={() => setInspectorId('')}
            >
              ✕
            </ClearButton>
          )}
          <Caret aria-hidden>▾</Caret>
          {isFetching && (
            <InlineLoader>
              <Loader size={18} thickness={4} />
            </InlineLoader>
          )}
        </SelectShell>
      </Field>

      <SectionLabel>Contact</SectionLabel>

      <Field>
        <FieldLabel>Activity Contact</FieldLabel>
        <SelectShell>
          <StyledSelect
            $clearable={contactId !== ''}
            value={contactId}
            onChange={e => handleContactChange(e.target.value)}
          >
            <option value="">Select a contact…</option>
            {contacts.map(contact => (
              <option key={contact.id} value={contact.id}>
                {contact.label}
              </option>
            ))}
          </StyledSelect>
          {contactId !== '' && (
            <ClearButton
              type="button"
              aria-label="Clear contact"
              onClick={() => handleContactChange('')}
            >
              ✕
            </ClearButton>
          )}
          <Caret aria-hidden>▾</Caret>
          {contactsLoading && (
            <InlineLoader>
              <Loader size={18} thickness={4} />
            </InlineLoader>
          )}
        </SelectShell>
      </Field>

      <TwoCol>
        <Field>
          <FieldLabel>
            Cell Phone
            {contactId !== '' && <RequiredStar>*</RequiredStar>}
          </FieldLabel>
          <StyledInput
            type="tel"
            value={cellPhone}
            disabled={contactId === ''}
            $error={cellPhoneError !== ''}
            onChange={e => {
              setCellPhone(e.target.value);
              setCellPhoneError(
                e.target.value.trim() === ''
                  ? 'Cell phone is a required field'
                  : '',
              );
            }}
            placeholder={contactId === '' ? 'Select a contact first' : 'Cell phone'}
          />
          {cellPhoneError && <FieldError>{cellPhoneError}</FieldError>}
        </Field>
        <Field>
          <FieldLabel>
            Phone Number
            {contactId !== '' && <RequiredStar>*</RequiredStar>}
          </FieldLabel>
          <StyledInput
            type="tel"
            value={phoneNumber}
            disabled={contactId === ''}
            $error={phoneNumberError !== ''}
            onChange={e => {
              setPhoneNumber(e.target.value);
              setPhoneNumberError(
                e.target.value.trim() === ''
                  ? 'Phone number is a required field'
                  : '',
              );
            }}
            placeholder={contactId === '' ? 'Select a contact first' : 'Phone number'}
          />
          {phoneNumberError && <FieldError>{phoneNumberError}</FieldError>}
        </Field>
      </TwoCol>

      {skippedCount > 0 && (
        <Callout>
          <CalloutBody>
            <Strong>{skippedCount}</Strong> selected{' '}
            {skippedCount === 1 ? 'row' : 'rows'} cannot be scheduled and will be
            skipped.
          </CalloutBody>
        </Callout>
      )}

      {error && <ErrorText>{error}</ErrorText>}

      <Footer>
        <SecondaryButton type="button" onClick={onClose}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          type="button"
          onClick={handleSubmit}
          disabled={!isValid || isLoading}
        >
          {isLoading ? 'Scheduling…' : `Schedule ${count} ${inspectionLabel}`}
        </PrimaryButton>
      </Footer>
    </ModalBody>
  );
};

export default BulkScheduleInspectionsModal;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 420px;
  font-family: ${FONT_STACK};
  color: var(--mainText);

  @media (max-width: 540px) {
    min-width: 0;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-bottom: 20px;
  margin-bottom: 22px;
  border-bottom: 1px solid var(--border);
`;

const Eyebrow = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const Title = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.2;
`;

const Subtitle = styled.div`
  font-size: 0.875rem;
  line-height: 1.55;
  color: var(--neutral-600);
  margin-top: 4px;
`;

const Strong = styled.span`
  font-weight: 600;
  color: var(--mainText);
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  background: var(--primary-50);
  color: var(--primary-700);
  border: 1px solid var(--primary-100);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  vertical-align: 1px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
`;

const FieldLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
`;

// Required indicator per the design-system label spec.
const RequiredStar = styled.span`
  color: var(--danger-500);
  margin-left: 2px;
`;

const SectionLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
  margin-bottom: 14px;
  padding-top: 18px;
  border-top: 1px solid var(--border);
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
`;

const SelectShell = styled.div`
  position: relative;
  width: 100%;
`;

const StyledInput = styled.input<{ $error?: boolean }>`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid ${({ $error }) => ($error ? 'var(--danger-500)' : 'var(--border)')};
  border-radius: 8px;
  background: var(--neutral-0);
  font-family: ${FONT_STACK};
  font-size: 0.875rem;
  color: var(--mainText);
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: var(--neutral-400);
  }
  &:hover:not(:disabled) {
    border-color: ${({ $error }) =>
      $error ? 'var(--danger-500)' : 'var(--neutral-400)'};
  }
  &:focus {
    outline: none;
    border-color: ${({ $error }) =>
      $error ? 'var(--danger-500)' : 'var(--primary-600)'};
    box-shadow: var(--shadow-focus);
  }
  &:disabled {
    background: var(--neutral-100);
    color: var(--neutral-500);
    cursor: not-allowed;
  }
`;

const StyledSelect = styled.select<{ $clearable?: boolean }>`
  width: 100%;
  height: 40px;
  padding: ${({ $clearable }) => ($clearable ? '0 60px 0 12px' : '0 36px 0 12px')};
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  font-family: ${FONT_STACK};
  font-size: 0.875rem;
  color: var(--mainText);
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--neutral-400);
  }
  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const Caret = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--neutral-500);
  pointer-events: none;
  font-size: 0.875rem;
`;

// Clear "✕" for the Inspector / Activity Contact selects — mirrors the single
// edit form, where a selected value can be removed. Sits left of the caret.
const ClearButton = styled.button`
  position: absolute;
  right: 34px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--neutral-500);
  font-size: 0.75rem;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: var(--neutral-100);
    color: var(--neutral-700);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const InlineLoader = styled.div`
  position: absolute;
  right: 36px;
  top: 50%;
  transform: translateY(-50%);
`;

// react-datepicker renders a bare <input>; style it to match the design-system
// inputs (40px tall, 8px radius, focus ring).
const DatePickerShell = styled.div`
  width: 100%;

  .react-datepicker-wrapper,
  .react-datepicker__input-container {
    width: 100%;
  }

  input {
    width: 100%;
    height: 40px;
    padding: 0 12px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--neutral-0);
    font-family: ${FONT_STACK};
    font-size: 0.875rem;
    color: var(--mainText);
    box-sizing: border-box;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &::placeholder {
      color: var(--neutral-400);
    }
    &:hover {
      border-color: var(--neutral-400);
    }
    &:focus {
      outline: none;
      border-color: var(--primary-600);
      box-shadow: var(--shadow-focus);
    }
  }
`;

const Callout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  background: var(--warning-50, #fff7ed);
  border: 1px solid var(--warning-100, #fed7aa);
  border-left: 3px solid var(--warning-500, #f59e0b);
  border-radius: 8px;
  margin-bottom: 20px;
`;

const CalloutBody = styled.div`
  font-size: 0.875rem;
  color: var(--neutral-700);
  line-height: 1.5;
`;

// Under-field required message (design-system error spec: 0.75rem, danger-500).
const FieldError = styled.div`
  font-size: 0.75rem;
  color: var(--danger-500);
`;

const ErrorText = styled.div`
  font-size: 0.8125rem;
  color: var(--danger-500);
  margin-bottom: 16px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 18px;
  border-top: 1px solid var(--border);
`;

const buttonReset = `
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-transform: none;
  height: 40px;
  min-width: 120px;
  padding: 0 18px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const PrimaryButton = styled.button`
  ${buttonReset}
  border: none;
  background: var(--primary-600);
  color: var(--neutral-0);
  &:hover:not(:disabled) {
    background: var(--primary-700);
  }
  &:disabled {
    background: var(--neutral-200);
    color: var(--neutral-400);
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  ${buttonReset}
  background: var(--neutral-0);
  border: 1px solid var(--border);
  color: var(--neutral-700);
  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }
`;
